/**
 * Collection list pills carousel custom element.
 *
 * Thin wrapper around the globally loaded Swiper bundle (see snippets/scripts.liquid).
 * Deliberately extends HTMLElement instead of @theme/component: Swiper owns and mutates
 * the slide DOM, so it must not be reconciled by the theme's morph/ref machinery.
 *
 * Options are read off data attributes so the section can stay declarative.
 */
export class CollectionPillsCarousel extends HTMLElement {
  /** @type {import('swiper').Swiper | null} */
  swiper = null;

  /** @type {number | undefined} */
  #retryTimer = undefined;

  connectedCallback() {
    this.#mount();
  }

  disconnectedCallback() {
    clearTimeout(this.#retryTimer);
    this.#retryTimer = undefined;
    this.swiper?.destroy(true, true);
    this.swiper = null;
  }

  /**
   * Initialises Swiper, retrying briefly while the global bundle is still parsing.
   * @param {number} [attempt] - Current retry attempt.
   */
  #mount(attempt = 0) {
    const container = this.querySelector('.swiper');

    if (!container) return;

    if (typeof window.Swiper !== 'function') {
      // The bundle is deferred; give it a few frames before giving up.
      if (attempt >= 20) return;

      this.#retryTimer = setTimeout(() => this.#mount(attempt + 1), 50);
      return;
    }

    const slideCount = this.querySelectorAll('.swiper-slide').length;
    const desktopPerView = this.#slidesPerView(this.dataset.slidesDesktop);
    const mobilePerView = this.#slidesPerView(this.dataset.slidesMobile);
    // Swiper refuses to loop a track holding fewer slides than twice the visible
    // count. Collections are merchant-picked rather than cloned, so a short list
    // simply stops at its ends instead.
    const loop = this.dataset.loop === 'true' && slideCount >= Math.ceil(this.#loopReference(desktopPerView)) * 2;

    this.swiper = new window.Swiper(container, {
      slidesPerView: mobilePerView,
      spaceBetween: this.#numberAttribute('spaceBetweenMobile', 12),
      speed: this.#numberAttribute('transitionSpeed', 600),
      loop,
      // No rewind: wrapping at the ends would keep both arrows permanently
      // enabled, so an unlooped track could never show its disabled state.
      rewind: false,
      grabCursor: true,
      watchOverflow: true,
      a11y: {
        enabled: true,
        prevSlideMessage: this.dataset.previousLabel || 'Previous slide',
        nextSlideMessage: this.dataset.nextLabel || 'Next slide',
        paginationBulletMessage: this.dataset.bulletLabel || 'Go to slide {{index}}',
      },
      keyboard: { enabled: true, onlyInViewport: true },
      navigation: this.#navigationOptions(),
      pagination: this.#paginationOptions(),
      autoplay: this.#autoplayOptions(slideCount),
      // Swiper applies only the single highest matching breakpoint on top of the
      // base options — entries do not stack — so this one repeats every value it
      // needs to change at the width the section's CSS also switches at.
      breakpoints: {
        750: {
          slidesPerView: desktopPerView,
          spaceBetween: this.#numberAttribute('spaceBetween', 16),
        },
      },
    });
  }

  /**
   * Slides-per-view figure the loop test is measured against. `auto` has no
   * declared count, so a conservative estimate stands in for it.
   * @param {number | 'auto'} perView - The resolved slides-per-view value.
   * @returns {number} A numeric slides-per-view estimate.
   */
  #loopReference(perView) {
    return perView === 'auto' ? 3 : perView;
  }

  /**
   * Wires the arrows, which sit below the track.
   * @returns {object | false} Swiper navigation options, or false when there are no arrows.
   */
  #navigationOptions() {
    const prevEl = this.querySelector('.collection-pills__arrow--prev');
    const nextEl = this.querySelector('.collection-pills__arrow--next');

    if (!prevEl || !nextEl) return false;

    return {
      prevEl,
      nextEl,
      disabledClass: 'collection-pills__arrow--disabled',
      lockClass: 'collection-pills__arrow--locked',
    };
  }

  /**
   * Wires the bullets, which the section renders only when they are turned on.
   * @returns {object | false} Swiper pagination options, or false when there are no bullets.
   */
  #paginationOptions() {
    const el = this.querySelector('.collection-pills__pagination');

    if (!el) return false;

    return {
      el,
      clickable: true,
      bulletClass: 'collection-pills__bullet',
      bulletActiveClass: 'collection-pills__bullet--active',
    };
  }

  /**
   * Builds the autoplay config, which a single-slide track never needs.
   * @param {number} slideCount - Number of slides in the carousel.
   * @returns {object | false} Swiper autoplay options, or false when disabled.
   */
  #autoplayOptions(slideCount) {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reducedMotion || this.dataset.autoplay !== 'true' || slideCount < 2) return false;

    return {
      delay: this.#numberAttribute('autoplaySpeed', 5) * 1000,
      pauseOnMouseEnter: true,
      disableOnInteraction: false,
    };
  }

  /**
   * Reads a slides-per-view value, keeping Swiper's `auto` keyword intact so
   * content-sized pills can measure themselves.
   * @param {string | undefined} value - The raw data attribute value.
   * @returns {number | 'auto'} A Swiper `slidesPerView` value.
   */
  #slidesPerView(value) {
    if (!value || value === 'auto') return 'auto';

    const parsed = Number(value);

    return Number.isFinite(parsed) && parsed > 0 ? parsed : 'auto';
  }

  /**
   * Reads a numeric data attribute with a fallback.
   * @param {string} key - The camelCased dataset key.
   * @param {number} fallback - Value used when the attribute is missing or invalid.
   * @returns {number} The parsed number.
   */
  #numberAttribute(key, fallback) {
    const value = Number(this.dataset[key]);

    return Number.isFinite(value) && value >= 0 ? value : fallback;
  }
}

if (!customElements.get('collection-pills-carousel')) {
  customElements.define('collection-pills-carousel', CollectionPillsCarousel);
}
